import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * A list's page, page size and filters, kept in the address bar — so a reload,
 * the back button or a shared link lands on the same view.
 *
 *   const [query, setQuery] = useListQuery({ page: 1, limit: 20, status: "", q: "" });
 *   setQuery({ status: "open" })   // any filter change goes back to page 1
 *   setQuery({ page: 3 })
 *
 * Values equal to their default are left out of the address to keep it short.
 * page and limit come back as numbers, everything else as strings.
 */
const useListQuery = (defaults) => {
  const [params, setParams] = useSearchParams();

  const query = useMemo(() => {
    const out = {};
    for (const [key, fallback] of Object.entries(defaults)) {
      const raw = params.get(key);
      if (raw === null) out[key] = fallback;
      else if (typeof fallback === "number") out[key] = Math.max(1, parseInt(raw, 10) || fallback);
      else out[key] = raw;
    }
    return out;
    // defaults is a literal at the call site; its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, JSON.stringify(defaults)]);

  const setQuery = useCallback(
    (patch) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...patch };
          // A new filter or page size makes the old page number meaningless.
          if (!("page" in patch)) merged.page = defaults.page ?? 1;
          for (const [key, value] of Object.entries(merged)) {
            if (value === undefined || value === null || value === "" || value === defaults[key]) next.delete(key);
            else next.set(key, String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, JSON.stringify(defaults)],
  );

  return [query, setQuery];
};

/**
 * A search box's text, applied to the query once typing pauses — so each
 * keystroke doesn't fire a request. Returns [text, setText] for the input.
 */
export const useSearchInput = (value, apply, delay = 300) => {
  const [text, setText] = useState(value);
  const applied = useRef(value);

  // Follows the query when it changes from elsewhere (Clear filters, back
  // button) — but not when it's our own earlier text arriving while the user
  // has already typed more, which would snap the box back.
  useEffect(() => {
    if (value !== applied.current) {
      applied.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (text === value) return undefined;
    const t = setTimeout(() => {
      applied.current = text;
      apply(text);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return [text, setText];
};

export default useListQuery;
